'use client';

import { CONFIG } from '@/constants';
import { consoleLog } from '@/utils';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';

const { APP_API_VERSION, APP_UI_VERSION } = CONFIG;

const VERSION_STORAGE_KEY = 'fieldone:version-signature';

// Azure AD client id used to identify MSAL-owned storage entries.
const AZURE_CLIENT_ID = process.env.NEXT_PUBLIC_AZURE_AD_CLIENT_ID ?? '';

/**
 * Determines whether a storage key belongs to the MSAL auth cache (or is the
 * version signature itself) and must be preserved across version changes.
 *
 * MSAL persists tokens and accounts in localStorage (cacheLocation:
 * 'localStorage'). Wiping those entries logs the user out and, if it happens
 * mid sign-in, destroys the in-flight redirect state and causes a login loop.
 */
const isPreservedStorageKey = (key: string): boolean => {
  const lower = key.toLowerCase();

  if (key === VERSION_STORAGE_KEY) return true;
  if (lower.startsWith('msal.')) return true;
  if (lower.includes('login.windows.net')) return true;
  if (lower.includes('login.microsoftonline.com')) return true;
  if (AZURE_CLIENT_ID && key.includes(AZURE_CLIENT_ID)) return true;

  return false;
};

/**
 * Removes stale app-owned cache keys from a storage area while preserving MSAL
 * auth entries and the version signature.
 */
const clearAppCache = (storage: Storage): void => {
  const keysToRemove: string[] = [];

  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key && !isPreservedStorageKey(key)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => storage.removeItem(key));
};

/**
 * AppVersionGuard
 *
 * Clears stale app cache when the app or API version changes, while preserving
 * MSAL authentication tokens so users stay signed in across deploys.
 * Set NEXT_PUBLIC_APP_VERSION and/or NEXT_PUBLIC_API_VERSION in the environment.
 */
export function AppVersionGuard() {
  const pathname = usePathname();

  useEffect(() => {
    // Guard should only run in the browser
    if (typeof window === 'undefined') return;

    // Never touch storage while an auth redirect is being processed. Clearing
    // storage (or reloading) mid-flow destroys MSAL's interaction state and is
    // the classic cause of post-login redirect loops.
    if (pathname?.startsWith('/auth')) return;

    // Get versions from env or config fallback, prioritizing env for dynamic updates without code changes
    const appVersion = process.env.NEXT_PUBLIC_APP_VERSION ?? APP_UI_VERSION;
    const apiVersion = process.env.NEXT_PUBLIC_API_VERSION ?? APP_API_VERSION;

    consoleLog.info(`AppVersionGuard checking versions. App: ${appVersion}, API: ${apiVersion}`);

    // Eject early if neither version is set, to avoid unnecessary storage operations on first load
    if (!appVersion && !apiVersion) return;

    // Create a version signature and compare with stored value
    const nextSignature = `${appVersion ?? 'unknown'}|${apiVersion ?? 'unknown'}`;
    const storedSignature = localStorage.getItem(VERSION_STORAGE_KEY);

    // If the signature matches, no action is needed
    if (storedSignature === nextSignature) return;

    try {
      // First-ever load (no signature recorded yet): there is no stale app cache
      // to clear, and reloading here can interrupt the initial auth bootstrap.
      // Just record the current signature and exit.
      if (storedSignature === null) {
        localStorage.setItem(VERSION_STORAGE_KEY, nextSignature);
        return;
      }

      // Version has changed - drop stale app cache but preserve MSAL auth
      // tokens so the user stays signed in, then reload for fresh app data.
      clearAppCache(sessionStorage);
      clearAppCache(localStorage);
      localStorage.setItem(VERSION_STORAGE_KEY, nextSignature);
      window.location.reload();
    } catch {
      // Fail silently to avoid breaking boot
    }
  }, [pathname]);

  return null;
}
